import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getAccount, isLoggedIn } from '@/api';
import kitBlueSm from '@/assets/kits/kit-blue-sm.svg';
import kitWhiteSm from '@/assets/kits/kit-white-sm.svg';
import { formatStatPair, type LiveStatistics } from '@/domain/live-statistics';
import type { LiveClockState } from '@/domain/live-clock';
import { LmtWidgetType } from '@/domain/live';
import {
  buildLiveStreamIframeSrc,
  canShowMatchTracker,
  isLiveStreamConfigured,
} from '@/domain/lmt';
import { cn } from '@/lib/cn';
import { LiveClockText } from './LiveClockText';
import { Icon } from '../common/icon';
import { LiveMatchTracker } from './LiveMatchTracker';
import { LiveStreamPlayer } from './LiveStreamPlayer';

/** Which media panel is open beside the scoreboard (`hidden` = both closed). */
export type LiveMediaMode = 'lmt' | 'stream' | 'hidden';

export interface LiveEventHeaderProps {
  home: string;
  away: string;
  clock?: string;
  liveClockState?: LiveClockState;
  translatePeriod?: (key: string) => string;
  homeScore?: string;
  awayScore?: string;
  statistics?: LiveStatistics | null;
  eventId: number;
  sport: string;
  matchTrackerEnabled: boolean;
  theSportsId?: string | null;
  /** Event-level LiveStream flag from live details. */
  liveStream?: boolean;
  /** Preferred media panel when opening details (`?media=stream`). */
  initialMediaMode?: Exclude<LiveMediaMode, 'hidden'>;
  className?: string;
}

export function LiveEventHeader({
  home,
  away,
  clock,
  liveClockState,
  translatePeriod,
  homeScore = '0',
  awayScore = '0',
  statistics,
  eventId,
  sport,
  matchTrackerEnabled,
  theSportsId,
  liveStream = false,
  initialMediaMode,
  className,
}: LiveEventHeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const account = getAccount();
  const trackingType = account?.LiveMatchTrackingType ?? LmtWidgetType.None;
  const trackingKey = account?.LiveMatchTrackingKey?.trim() ?? '';

  const showLmt = canShowMatchTracker({
    trackingType,
    trackingKey,
    theSportsId,
    sport,
  });
  void matchTrackerEnabled;

  const streamConfigured = isLiveStreamConfigured(account);
  const showStreamControl = streamConfigured && liveStream && Boolean(theSportsId);
  const loggedIn = isLoggedIn();
  const streamSrc = useMemo(
    () =>
      showStreamControl && loggedIn
        ? buildLiveStreamIframeSrc({
            account,
            theSportsId,
            lang: i18n.language || 'en',
          })
        : null,
    [account, i18n.language, loggedIn, showStreamControl, theSportsId],
  );

  // LMT is visible by default when available (legacy opens the widget on details).
  const defaultMode: LiveMediaMode =
    initialMediaMode === 'stream' && showStreamControl && loggedIn
      ? 'stream'
      : showLmt
        ? 'lmt'
        : showStreamControl && loggedIn
          ? 'stream'
          : 'hidden';

  const [mediaMode, setMediaMode] = useState<LiveMediaMode>(defaultMode);

  useEffect(() => {
    setMediaMode(defaultMode);
  }, [defaultMode, eventId]);

  const corners = formatStatPair(statistics?.corners);
  const yellowCards = formatStatPair(statistics?.yellowCards);
  const redCards = formatStatPair(statistics?.redCards);
  const penalties = formatStatPair(statistics?.penalties);

  const activeMode: LiveMediaMode =
    mediaMode === 'stream' && showStreamControl && streamSrc
      ? 'stream'
      : mediaMode === 'lmt' && showLmt
        ? 'lmt'
        : 'hidden';

  const toggleLmt = () => {
    if (!showLmt) return;
    setMediaMode((current) => (current === 'lmt' ? 'hidden' : 'lmt'));
  };

  const toggleStream = () => {
    if (!showStreamControl) return;
    if (!loggedIn) {
      void navigate({ to: '/login' });
      return;
    }
    setMediaMode((current) => (current === 'stream' ? 'hidden' : 'stream'));
  };

  const mediaOpen = activeMode !== 'hidden';

  return (
    <section
      className={cn(
        'flex flex-col overflow-hidden bg-bg-surface',
        // Keep rounded corners for LMT / score-only; square when stream is open.
        activeMode !== 'stream' && 'rounded-xl',
        // Desktop: scoreboard left, LMT/stream right.
        mediaOpen && 'lg:flex-row lg:items-stretch',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-col items-center gap-3 p-3">
          <div className="flex w-full items-center justify-center">
            <div className="flex-1" />
            <div className="flex h-7 items-center gap-1.5 rounded-full bg-live-bg px-3">
              <Icon
                name="live-icon"
                size={14}
                className="block shrink-0 self-center text-text-primary"
              />
              {clock || liveClockState ? (
                <LiveClockText
                  liveClockState={liveClockState}
                  clock={clock}
                  translatePeriod={translatePeriod}
                  className="flex items-center text-[11px] leading-none font-bold whitespace-nowrap text-text-primary"
                />
              ) : null}
            </div>
            <div className="flex flex-1 items-center justify-end gap-1">
              {showLmt ? (
                <HeaderActionButton
                  label={t('Pitch', { defaultValue: 'Pitch' })}
                  pressed={activeMode === 'lmt'}
                  onClick={toggleLmt}
                >
                  <PitchGlyph />
                </HeaderActionButton>
              ) : null}
              {showStreamControl ? (
                <HeaderActionButton
                  label={t('LiveStream', { defaultValue: 'Live stream' })}
                  pressed={activeMode === 'stream'}
                  onClick={toggleStream}
                >
                  <Icon name="monitor-play" size={12} />
                </HeaderActionButton>
              ) : null}
            </div>
          </div>

          <div className="flex w-full items-center justify-center gap-2">
            <Competitor name={home} kitSrc={kitWhiteSm} />
            <div className="flex shrink-0 items-center gap-1.5">
              <ScoreBox score={homeScore} />
              <ScoreBox score={awayScore} />
            </div>
            <Competitor name={away} kitSrc={kitBlueSm} />
          </div>
        </div>

        {/* Same flex columns as the competitor row so stats sit under each team. */}
        <div className="flex w-full items-center justify-center gap-2 px-3 py-2">
          <div className="flex min-w-0 flex-1 justify-center">
            <StatCluster
              corners={corners.home}
              yellow={yellowCards.home}
              red={redCards.home}
              penalties={penalties.home}
            />
          </div>
          <div
            className="invisible flex shrink-0 items-center gap-1.5"
            aria-hidden
          >
            <ScoreBox score={homeScore} />
            <ScoreBox score={awayScore} />
          </div>
          <div className="flex min-w-0 flex-1 justify-center">
            <StatCluster
              corners={corners.away}
              yellow={yellowCards.away}
              red={redCards.away}
              penalties={penalties.away}
              reverse
            />
          </div>
        </div>
      </div>

      {mediaOpen ? (
        <div className="w-full shrink-0 overflow-hidden bg-bg-surface lg:w-[390px]">
          {activeMode === 'lmt' ? (
            <LiveMatchTracker
              eventId={eventId}
              sport={sport}
              matchTrackerEnabled={matchTrackerEnabled}
              theSportsId={theSportsId}
            />
          ) : null}
          {activeMode === 'stream' && streamSrc ? <LiveStreamPlayer src={streamSrc} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function HeaderActionButton({
  children,
  label,
  pressed,
  onClick,
}: {
  children: ReactNode;
  label: string;
  pressed?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'flex size-5 items-center justify-center rounded-sm border text-icon-muted',
        pressed ? 'border-action-secondary text-action-secondary' : 'border-white/16',
      )}
    >
      {children}
    </button>
  );
}

function Competitor({ name, kitSrc }: { name: string; kitSrc: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2">
      <span className="relative size-8 shrink-0 overflow-clip">
        <img src={kitSrc} alt="" className="size-full object-contain" />
      </span>
      <p className="w-full text-center text-caption font-bold text-text-primary">{name}</p>
    </div>
  );
}

function ScoreBox({ score }: { score: string }) {
  return (
    <div className="flex size-9 items-center justify-center rounded-lg bg-bg-elevated">
      <span className="text-[18px] font-bold tabular-nums text-text-primary">{score}</span>
    </div>
  );
}

function StatCluster({
  corners,
  yellow,
  red,
  penalties,
  reverse = false,
}: {
  corners: string;
  yellow: string;
  red: string;
  penalties: string;
  reverse?: boolean;
}) {
  const items = [
    { key: 'corners', value: corners, icon: <CornerIcon reverse={reverse} /> },
    { key: 'red', value: red, icon: <CardIcon tone="danger" /> },
    { key: 'yellow', value: yellow, icon: <CardIcon tone="warning" /> },
    { key: 'penalties', value: penalties, icon: <PenaltyIcon /> },
  ];

  const ordered = reverse ? [...items].reverse() : items;

  return (
    <div className="flex items-center gap-3">
      {ordered.map((item) => (
        <div key={item.key} className="flex w-[22px] flex-col items-center gap-1">
          <span className="flex size-4 items-center justify-center">{item.icon}</span>
          <span className="text-[11px] font-bold text-text-primary">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function CornerIcon({ reverse }: { reverse?: boolean }) {
  return (
    <span className="relative size-3">
      <span
        className={cn(
          'absolute top-0 h-2 w-0.5 bg-text-primary',
          reverse ? 'right-0' : 'left-0',
        )}
      />
      <span
        className={cn(
          'absolute bottom-0 h-0.5 w-2 bg-text-primary',
          reverse ? 'right-0' : 'left-0',
        )}
      />
    </span>
  );
}

function CardIcon({ tone }: { tone: 'danger' | 'warning' }) {
  return (
    <span
      className={cn(
        'inline-block h-3 w-2 rotate-12 rounded-[1.5px]',
        tone === 'danger' ? 'bg-status-danger' : 'bg-status-warning',
      )}
    />
  );
}

function PenaltyIcon() {
  return (
    <svg viewBox="0 0 12 12" className="size-3 text-text-primary" aria-hidden>
      <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1" />
      <path d="M4 6h4" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function PitchGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="size-3" aria-hidden>
      <rect x="1" y="2" width="10" height="8" rx="0.5" fill="none" stroke="currentColor" strokeWidth="0.8" />
      <line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" strokeWidth="0.8" />
    </svg>
  );
}
